import React, { useEffect, useMemo, useState } from 'react';
import { Menu, Badge, Dropdown, List, Avatar, Button, Empty, Spin, message } from 'antd';
import {
    BellOutlined,
    MailOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    DollarOutlined,
    DesktopOutlined,
    InfoCircleOutlined,
    PrinterOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { defineMessages, FormattedDate, FormattedTime, useIntl } from 'react-intl';
import moment from 'moment';
import NotificationService from 'services/NotificationService';
import { useSelector } from 'react-redux';
import { env } from 'configs/EnvironmentConfig';
import usePushNotifications from 'hooks/usePushNotifications';
import IntlMessage from 'components/util-components/IntlMessage';
import styles from './nav-notification.module.css';
import { TOPUP_CONFIRMED_TYPE, refreshAfterTopupConfirmed } from 'constants/BankReconciliationConstant';

// defaultMessage is shown until the key is added to the backend-served locale files
const messages = defineMessages({
    title: { id: 'notification.dropdown.title', defaultMessage: 'Notifications' },
    newCount: { id: 'notification.dropdown.new_count', defaultMessage: '{count} new' },
    markAllRead: { id: 'notification.dropdown.mark_all_read', defaultMessage: 'Mark all as read' },
    empty: { id: 'notification.dropdown.empty', defaultMessage: "You're all caught up" },
    viewAll: { id: 'notification.dropdown.view_all', defaultMessage: 'View all notifications' },
    untitled: { id: 'notification.dropdown.untitled', defaultMessage: 'Notification' },
    markReadFailed: { id: 'notification.dropdown.mark_read_failed', defaultMessage: 'Failed to mark notification as read' },
    markAllReadSuccess: { id: 'notification.dropdown.mark_all_read_success', defaultMessage: 'All notifications marked as read' },
    markAllReadFailed: { id: 'notification.dropdown.mark_all_read_failed', defaultMessage: 'Failed to mark notifications as read' },
    pushTitle: { id: 'notification.push.enable_title', defaultMessage: 'Turn on desktop alerts' },
    pushDescription: { id: 'notification.push.enable_description', defaultMessage: 'Get notified even when this tab is in the background' },
    pushEnable: { id: 'notification.push.enable', defaultMessage: 'Enable' },
    pushBlocked: { id: 'notification.push.blocked', defaultMessage: 'Desktop alerts are blocked. Allow notifications for this site in your browser settings.' },
});

const PRODUCT_REQUEST_TYPE = 'product_request';

const isProductRequestType = type => typeof type === 'string' && type.startsWith(PRODUCT_REQUEST_TYPE);

const getIcon = icon => {
    if (isProductRequestType(icon)) {
        return <PrinterOutlined />;
    }
    switch (icon) {
        case "topup_demand":
        case "topup_demand_test":
            return <DollarOutlined />;
        case 'alert':
            return <WarningOutlined />;
        case 'check':
        case TOPUP_CONFIRMED_TYPE:
            return <CheckCircleOutlined />
        default:
            return <MailOutlined />;
    }
}

const getColor = (type) => {
    //available colors: blue, purple, cyan, green, magenta, pink, red, orange , volcano , yellow , geekblue , lime , gold , primary , secondary , success , info , warning , danger , gray , gray-light , gray-lighter , gray-lightest ,gray-dark
    if (isProductRequestType(type)) {
        return 'cyan';
    }
    switch (type) {
        case 'topup_demand':
            return 'gold';
        case 'topup_demand_test':
            return 'lime';
        case 'alert':
            return 'red';
        case 'check':
        case TOPUP_CONFIRMED_TYPE:
            return 'green';
        case 'info':
            return 'blue';
        case 'warning':
            return 'yellow';
        case 'danger':
            return 'red';
        case 'gray':
            return 'gray';
        default:
            return 'gray';
    }
}

const normalizeResponseData = (payload) => payload?.data || payload || {};

// Time for today's notifications, day and month for older ones; full date on hover
const NotificationTime = ({ date }) => {
    const created = moment(date);
    if (!created.isValid()) {
        return null;
    }
    return (
        <small className={`text-gray-light ${styles.itemTime}`} title={created.format('YYYY-MM-DD HH:mm')}>
            {created.isSame(moment(), 'day')
                ? <FormattedTime value={created.toDate()} hour="2-digit" minute="2-digit" hourCycle="h23" />
                : <FormattedDate value={created.toDate()} day="numeric" month="short" />}
        </small>
    );
}

const NotificationItem = ({ item, onClick }) => {
    const type = item.notification?.type;
    const isUnread = item.status === 'unread';
    return (
        <List.Item className={`list-clickable ${styles.item} ${isUnread ? styles.unread : ''}`} onClick={() => onClick(item)}>
            <Avatar className={`ant-avatar-${getColor(type)} flex-shrink-0`} icon={getIcon(type)} />
            <div className={styles.itemContent}>
                <div className={styles.itemHeader}>
                    <span className={`text-truncate ${isUnread ? 'font-weight-bold text-dark' : 'font-weight-semibold text-gray-light'}`}>
                        {item.notification?.title || <IntlMessage {...messages.untitled} />}
                    </span>
                    <NotificationTime date={item.createdAt} />
                </div>
                {item.notification?.message &&
                    <div className={`text-gray-light ${styles.itemMessage}`}>{item.notification.message}</div>
                }
            </div>
            {isUnread && <span className={styles.unreadDot} />}
        </List.Item>
    );
}

const PushAlertsPrompt = ({ permission, onEnable }) => {
    if (permission === 'default') {
        return (
            <div className={styles.pushPrompt}>
                <DesktopOutlined className={styles.pushIcon} />
                <div className={styles.pushText}>
                    <div className="font-weight-semibold text-dark"><IntlMessage {...messages.pushTitle} /></div>
                    <small className="text-gray-light"><IntlMessage {...messages.pushDescription} /></small>
                </div>
                <Button type="primary" size="small" onClick={onEnable}>
                    <IntlMessage {...messages.pushEnable} />
                </Button>
            </div>
        );
    }
    if (permission === 'denied') {
        return (
            <div className={`${styles.pushPrompt} ${styles.pushPromptBlocked}`}>
                <InfoCircleOutlined className={styles.pushIcon} />
                <small className={`text-gray-light ${styles.pushText}`}><IntlMessage {...messages.pushBlocked} /></small>
            </div>
        );
    }
    return null;
}

export const NavNotification = () => {
    const history = useHistory();
    const intl = useIntl();
    const queryClient = useQueryClient();
    const [visible, setVisible] = useState(false);

    const listRequest = useMemo(() => ({
        pagination: {
            current: 1,
            pageSize: 10,
        },
    }), []);

    const { data: notificationsResponse, isLoading: isLoadingNotifications } = useQuery(
        NotificationService.list(listRequest)
    );
    const { data: unreadResponse, isLoading: isLoadingUnreadCount } = useQuery(
        NotificationService.getUnreadCount()
    );

    const userCode = useSelector(state => state.auth.code);
    const { supported: isPushSupported, permission: pushPermission, enable: enablePush } = usePushNotifications(userCode);

    useEffect(() => {
        const socket = new WebSocket(env.WEB_SOCKET_URL);  // Change to your WebSocket server URL

        socket.onopen = () => {
            console.log('Connected to WebSocket server');
            socket.send(JSON.stringify({
                action: 'subscribe',
                user_code: userCode
            }));
        };

        socket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            if (message?.data?.type === 'topup_demand' || message?.data?.type === 'topup_demand_test') {
                const audio = new Audio('/audio/notification_1.mp3'); // Adjust path if needed
                audio.play().catch((err) => console.error("Audio play failed:", err));
            } else if (message?.type !== "subscribed") {
                const audio = new Audio('/audio/notification.mp3'); // Adjust path if needed
                audio.play().catch((err) => console.error("Audio play failed:", err));
            }

            queryClient.invalidateQueries({ queryKey: ['get_notification_unread_count'] });
            queryClient.invalidateQueries({ queryKey: ['get_all_notifications'] });
            if (isProductRequestType(message?.data?.type)) {
                queryClient.invalidateQueries({ queryKey: ['get_all_product_requests'] });
                queryClient.invalidateQueries({ queryKey: ['get_product_request_by_id'] });
            }
            if (message?.data?.type === TOPUP_CONFIRMED_TYPE) {
                refreshAfterTopupConfirmed(queryClient);
            }
        };

        socket.onclose = () => {
            console.log('Disconnected from WebSocket server');
        };

        // Clean up when the component is unmounted
        return () => {
            socket.close();
        };
    }, [userCode]);

    const notificationsData = normalizeResponseData(notificationsResponse);
    const unreadData = normalizeResponseData(unreadResponse);

    const notifications = notificationsData?.results || [];
    const unreadCountFromList = notifications.filter(item => item.status === 'unread').length;
    const unreadCount = Number.isFinite(unreadData?.unreadCount) ? unreadData.unreadCount : unreadCountFromList;

    const refreshNotifications = () => {
        queryClient.invalidateQueries(['get_all_notifications']);
        queryClient.invalidateQueries(['get_notification_unread_count']);
    };

    const markReadMutation = useMutation({
        mutationFn: NotificationService.markRead,
        onSuccess: refreshNotifications,
        onError: (error) => {
            message.error(error.message || intl.formatMessage(messages.markReadFailed));
        }
    });

    const markAllReadMutation = useMutation({
        mutationFn: NotificationService.markAllRead,
        onSuccess: () => {
            refreshNotifications();
            message.success(intl.formatMessage(messages.markAllReadSuccess));
        },
        onError: (error) => {
            message.error(error.message || intl.formatMessage(messages.markAllReadFailed));
        }
    });

    const handleVisibleChange = (flag) => {
        setVisible(flag);
    }

    const handleNotificationClick = (item) => {
        if (item.status === 'unread' && item.id) {
            markReadMutation.mutate(item.id);
        }
        setVisible(false);
        console.log(item?.notification?.type);
        if (item?.notification?.type === 'topup_demand' || item?.notification?.type === 'topup_demand_test' || item?.notification?.type === TOPUP_CONFIRMED_TYPE) {
            history.push('/app/credit-request/list');
        } else if (item?.notification?.type === PRODUCT_REQUEST_TYPE) {
            const productRequestId = item.notification.payload?.productRequestId;
            history.push(productRequestId ? `/app/product-requests/list?id=${productRequestId}` : '/app/product-requests/list');
        } else {
            history.push('/app/profile/notification');
        }

    }

    const isReadAllLoading = markAllReadMutation.isLoading;
    const isDropdownLoading = isLoadingNotifications || isLoadingUnreadCount;
    const hasUnread = unreadCount > 0;

    const notificationList = (
        <div className={`nav-dropdown nav-notification ${styles.dropdown}`}>
            <div className="nav-notification-header d-flex justify-content-between align-items-center">
                <div className={styles.headerTitle}>
                    <h4 className="mb-0"><IntlMessage {...messages.title} /></h4>
                    {!isDropdownLoading && hasUnread &&
                        <small className="text-gray-light"><IntlMessage {...messages.newCount} values={{ count: unreadCount }} /></small>
                    }
                </div>
                <Button
                    className={hasUnread ? 'text-primary' : ''}
                    type="text"
                    size="small"
                    onClick={() => markAllReadMutation.mutate()}
                    loading={isReadAllLoading}
                    disabled={!hasUnread}
                >
                    <IntlMessage {...messages.markAllRead} />
                </Button>
            </div>
            {isPushSupported && <PushAlertsPrompt permission={pushPermission} onEnable={enablePush} />}
            <div className="nav-notification-body">
                {isLoadingNotifications ?
                    <div className={styles.state}><Spin /></div>
                    : notifications.length > 0 ?
                        <List
                            size="small"
                            itemLayout="horizontal"
                            rowKey="id"
                            dataSource={notifications}
                            renderItem={item => <NotificationItem item={item} onClick={handleNotificationClick} />}
                        />
                        :
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<IntlMessage {...messages.empty} />} />
                }
            </div>
            {
                notifications.length > 0 ?
                    <div className="nav-notification-footer">
                        <Button className="d-block w-100" type="link" onClick={() => {
                            setVisible(false);
                            history.push('/app/profile/notification');
                        }}>
                            <IntlMessage {...messages.viewAll} />
                        </Button>
                    </div>
                    :
                    null
            }
        </div>
    );

    return (
        <Dropdown
            placement="bottomRight"
            overlay={notificationList}
            onVisibleChange={handleVisibleChange}
            visible={visible}
            trigger={['click']}
        >
            <Menu mode="horizontal">
                <Menu.Item key="notification">
                    <Badge count={isDropdownLoading ? 0 : unreadCount}>
                        <BellOutlined className="nav-icon mx-auto" type="bell" />
                    </Badge>
                </Menu.Item>
            </Menu>
        </Dropdown>
    )
}


export default NavNotification;
